/**
 * Time Tracking Page - Stundenerfassung für Trainer
 * Admin-Übersicht und Trainer-Eingabe
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { 
  Clock, Calendar, Plus, Edit, Trash2, Download, Mail, Settings,
  User, Users, Palmtree, Thermometer, FileText, CheckCircle, AlertCircle,
  ChevronLeft, ChevronRight, TrendingUp, TrendingDown, RefreshCw,
  CalendarCheck, Lock, Unlock, ArrowRightLeft, CreditCard, DollarSign, Send, Percent, ShieldCheck, Building2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Abwesenheitstypen
const ABSENCE_TYPES = [
  { id: 'vacation', name: 'Ferien', icon: '🏖️', color: 'bg-blue-100 text-blue-800' },
  { id: 'sick', name: 'Krankheit', icon: '🤒', color: 'bg-red-100 text-red-800' },
  { id: 'unpaid', name: 'Unbezahlt', icon: '💰', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'military', name: 'Militär', icon: '🎖️', color: 'bg-green-100 text-green-800' },
  { id: 'other', name: 'Andere', icon: '📋', color: 'bg-gray-100 text-gray-800' }
];

// Eintragstypen
const ENTRY_TYPES = [
  { id: 'work', name: 'Arbeit', icon: '💼' },
  { id: 'training', name: 'Training', icon: '🏋️' },
  { id: 'meeting', name: 'Meeting', icon: '👥' },
  { id: 'other', name: 'Andere', icon: '📝' }
];

const TimeTrackingPage = ({ embedded = false, mode = 'all' }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    mode === 'payroll' ? 'payroll' : ((!embedded && searchParams.get('tab')) || 'overview')
  );
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [entries, setEntries] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // User role and permissions
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Im Hub kann sich der Modus (Stunden/Lohn) ändern, ohne dass die Komponente
  // neu mountet → aktiven Unter-Tab entsprechend nachziehen.
  useEffect(() => {
    if (mode === 'payroll') setActiveTab('payroll');
    else if (mode === 'hours') setActiveTab((t) => (t === 'payroll' ? 'overview' : t));
  }, [mode]);
  const [canViewAll, setCanViewAll] = useState(false);
  
  // Dialog States
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [showAbsenceDialog, setShowAbsenceDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showYearCloseDialog, setShowYearCloseDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingAccounting, setSendingAccounting] = useState(false);
  const [yearSummary, setYearSummary] = useState(null);
  const [yearClosings, setYearClosings] = useState([]);
  const [closingYear, setClosingYear] = useState(false);
  
  // Email Form State
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    additional_message: ''
  });
  
  // Form States
  const [entryForm, setEntryForm] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '08:00',
    end_time: '17:00',
    break_minutes: 60,
    entry_type: 'work',
    notes: ''
  });
  
  const [absenceForm, setAbsenceForm] = useState({
    start_date: '',
    end_date: '',
    absence_type: 'vacation',
    notes: ''
  });
  
  const [settingsForm, setSettingsForm] = useState({
    weekly_hours: 42,
    vacation_days_per_year: 25,
    start_date: '',
    initial_balance_hours: 0,
    initial_balance_note: '',
    initial_balance_date: '',
    vacation_carryover_days: 0,
    vacation_carryover_note: ''
  });
  
  // Year-End Closing Form
  const [yearCloseForm, setYearCloseForm] = useState({
    final_balance_hours: 0,
    carry_over_hours: 0,
    note: ''
  });

  // Payroll States
  const [payrollSettings, setPayrollSettings] = useState(null);
  const [bonusConfig, setBonusConfig] = useState(null);
  const [monthlyPayroll, setMonthlyPayroll] = useState(null);
  const [payrollAutomation, setPayrollAutomation] = useState(null);
  const [payrollAutomationSaving, setPayrollAutomationSaving] = useState(false);
  const [showPayrollSettingsDialog, setShowPayrollSettingsDialog] = useState(false);
  const [showBonusDialog, setShowBonusDialog] = useState(false);
  const [sendingPayrollEmail, setSendingPayrollEmail] = useState(false);
  const [payrollForm, setPayrollForm] = useState({
    wage_type: 'fixed', hourly_wage: 0,
    monthly_salary: 0, familienzulage: 0, bvg_amount: 0, pk_employer_amount: 0,
    ahv_iv_eo_rate: 5.3, alv_rate: 1.1, nbuv_rate: 1.947,
    uvgz_rate: 0.233, ktg_rate: 0.625, bvg_amount: 0,
    quellensteuer_enabled: false, quellensteuer_rate: 0,
    bank_name: '', bank_iban: '', ahv_number: '', notes: ''
  });
  const [bonusForm, setBonusForm] = useState({
    tiers: [], sick_day_deduction_fraction: 0.25
  });
  // Zentrale Standard-Abzugssätze + Freigabe-Workflow
  const [showDefaultRatesDialog, setShowDefaultRatesDialog] = useState(false);
  const [defaultRatesForm, setDefaultRatesForm] = useState({
    ahv_iv_eo_rate: 5.3, alv_rate: 1.1, nbuv_rate: 1.947, uvgz_rate: 0.233, ktg_rate: 0.625
  });
  const [defaultRatesPending, setDefaultRatesPending] = useState(null);
  const [savingDefaultRates, setSavingDefaultRates] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    // Decode token to get user info
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser(payload);
        const userRole = payload.role || payload.type;
        setIsAdmin(userRole === 'admin');
        // Check if user can view all timetracking (admin or has permission)
        const permissions = payload.permissions || {};
        setCanViewAll(userRole === 'admin' || permissions.can_view_all_timetracking);
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    }
    loadTrainers();
    loadHolidays(selectedYear);
  }, []);

  useEffect(() => {
    if (selectedTrainer) {
      loadData();
      loadYearData();
    }
  }, [selectedTrainer, selectedMonth, selectedYear]);

  const loadTrainers = async () => {
    try {
      const response = await axios.get(`${API}/timetracking/trainers`, { headers });
      let trainerList = response.data;
      
      // Wenn kein Admin und keine "alle sehen"-Berechtigung, nur eigene anzeigen
      if (!isAdmin && !canViewAll && currentUser) {
        const currentUserId = currentUser.sub || currentUser.user_id;
        trainerList = trainerList.filter(t => 
          t.trainer.id === currentUserId || t.trainer.user_id === currentUserId
        );
      }
      
      setTrainers(trainerList);
      if (trainerList.length > 0 && !selectedTrainer) {
        setSelectedTrainer(trainerList[0].trainer.id);
      }
    } catch (error) {
      console.error('Error loading trainers:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Lade Zeiteinträge
      const entriesRes = await axios.get(`${API}/timetracking/entries`, {
        headers,
        params: { trainer_id: selectedTrainer, month: selectedMonth, year: selectedYear }
      });
      setEntries(entriesRes.data.entries || []);
      
      // Lade Abwesenheiten
      const absencesRes = await axios.get(`${API}/timetracking/absences`, {
        headers,
        params: { trainer_id: selectedTrainer, year: selectedYear }
      });
      setAbsences(absencesRes.data.absences || []);
      
      // Lade Monatsbericht
      const reportRes = await axios.get(`${API}/timetracking/reports/monthly`, {
        headers,
        params: { trainer_id: selectedTrainer, month: selectedMonth, year: selectedYear }
      });
      setMonthlyReport(reportRes.data);
      
      // Lade Trainer-Einstellungen
      const settingsRes = await axios.get(`${API}/timetracking/trainers/${selectedTrainer}/settings`, { headers });
      setSettingsForm(settingsRes.data);
      
      // Lade Lohn-Einstellungen
      if (isAdmin) {
        try {
          const [payrollRes, bonusRes, payrollCalcRes, automationRes] = await Promise.all([
            axios.get(`${API}/timetracking/payroll/settings/${selectedTrainer}`, { headers }),
            axios.get(`${API}/timetracking/payroll/bonus/${selectedTrainer}`, { headers }),
            axios.get(`${API}/timetracking/payroll/monthly/${selectedTrainer}/${selectedYear}/${selectedMonth}`, { headers }),
            axios.get(`${API}/settings/payroll-automation`, { headers }).catch(() => ({ data: null })),
          ]);
          setPayrollSettings(payrollRes.data);
          setPayrollForm(payrollRes.data);
          setBonusConfig(bonusRes.data);
          setBonusForm(bonusRes.data);
          setMonthlyPayroll(payrollCalcRes.data);
          if (automationRes?.data) setPayrollAutomation(automationRes.data);
        } catch (e) { console.error('Payroll load error:', e); }
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHolidays = async (year) => {
    try {
      const response = await axios.get(`${API}/timetracking/holidays/${year}`, { headers });
      setHolidays(response.data.holidays || []);
    } catch (error) {
      console.error('Error loading holidays:', error);
    }
  };

  const loadYearData = async () => {
    if (!selectedTrainer) return;
    try {
      // Lade Jahresübersicht
      const summaryRes = await axios.get(`${API}/timetracking/year-summary/${selectedTrainer}/${selectedYear}`, { headers });
      setYearSummary(summaryRes.data);
      
      // Lade alle Jahresabschlüsse
      const closingsRes = await axios.get(`${API}/timetracking/year-closings/${selectedTrainer}`, { headers });
      setYearClosings(closingsRes.data.closings || []);
    } catch (error) {
      console.error('Error loading year data:', error);
    }
  };

  const handleCreateEntry = async () => {
    try {
      await axios.post(`${API}/timetracking/entries`, entryForm, {
        headers,
        params: { trainer_id: selectedTrainer }
      });
      toast.success('Zeiteintrag erstellt');
      setShowEntryDialog(false);
      loadData();
      resetEntryForm();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleUpdateEntry = async () => {
    try {
      await axios.put(`${API}/timetracking/entries/${editingEntry.id}`, entryForm, { headers });
      toast.success('Zeiteintrag aktualisiert');
      setShowEntryDialog(false);
      setEditingEntry(null);
      loadData();
      resetEntryForm();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/timetracking/entries/${entryId}`, { headers });
      toast.success('Eintrag gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleCreateAbsence = async () => {
    try {
      const response = await axios.post(`${API}/timetracking/absences`, absenceForm, {
        headers,
        params: { trainer_id: selectedTrainer }
      });
      const result = response.data;
      toast.success(`Abwesenheit erstellt: ${result.calculated_days} Arbeitstage`);
      if (result.holidays_excluded?.length > 0) {
        toast.info(`${result.holidays_excluded.length} Feiertag(e) ausgenommen`);
      }
      setShowAbsenceDialog(false);
      loadData();
      resetAbsenceForm();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleDeleteAbsence = async (absenceId) => {
    if (!window.confirm('Abwesenheit wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/timetracking/absences/${absenceId}`, { headers });
      toast.success('Abwesenheit gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/timetracking/trainers/${selectedTrainer}/settings`, {
        trainer_id: selectedTrainer,
        ...settingsForm
      }, { headers });
      toast.success('Einstellungen gespeichert');
      setShowSettingsDialog(false);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleCloseMonth = async () => {
    if (!window.confirm(`Monat ${selectedMonth}/${selectedYear} wirklich abschliessen? Dies kann nicht rückgängig gemacht werden.`)) return;
    try {
      await axios.post(`${API}/timetracking/reports/monthly/close`, null, {
        headers,
        params: { trainer_id: selectedTrainer, month: selectedMonth, year: selectedYear }
      });
      toast.success('Monat abgeschlossen');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Abschliessen');
    }
  };

  const openYearCloseDialog = () => {
    if (yearSummary) {
      setYearCloseForm({
        final_balance_hours: yearSummary.total_balance || 0,
        carry_over_hours: yearSummary.total_balance || 0,
        note: ''
      });
    }
    setShowYearCloseDialog(true);
  };

  const handleCloseYear = async () => {
    if (!window.confirm(`Jahr ${selectedYear} wirklich abschliessen? Der Übertrag von ${yearCloseForm.carry_over_hours}h wird ins Jahr ${selectedYear + 1} übernommen.`)) return;
    
    setClosingYear(true);
    try {
      await axios.post(`${API}/timetracking/year-closings/${selectedTrainer}/${selectedYear}`, {
        trainer_id: selectedTrainer,
        year: selectedYear,
        final_balance_hours: yearCloseForm.final_balance_hours,
        carry_over_hours: yearCloseForm.carry_over_hours,
        note: yearCloseForm.note || null
      }, { headers });
      
      toast.success(`Jahr ${selectedYear} erfolgreich abgeschlossen! Übertrag: ${yearCloseForm.carry_over_hours >= 0 ? '+' : ''}${yearCloseForm.carry_over_hours}h`);
      setShowYearCloseDialog(false);
      loadYearData();
      loadData();
    } catch (error) {
      console.error('Year close error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Jahresabschluss');
    } finally {
      setClosingYear(false);
    }
  };

  const handleDeleteYearClosing = async (year) => {
    if (!window.confirm(`Jahresabschluss ${year} wirklich löschen? Dies hebt die Sperrung auf.`)) return;
    
    try {
      await axios.delete(`${API}/timetracking/year-closings/${selectedTrainer}/${year}`, { headers });
      toast.success(`Jahresabschluss ${year} gelöscht`);
      loadYearData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const resetEntryForm = () => {
    setEntryForm({
      date: new Date().toISOString().split('T')[0],
      start_time: '08:00',
      end_time: '17:00',
      break_minutes: 60,
      entry_type: 'work',
      notes: ''
    });
  };

  const resetAbsenceForm = () => {
    setAbsenceForm({
      start_date: '',
      end_date: '',
      absence_type: 'vacation',
      notes: ''
    });
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      date: entry.date,
      start_time: entry.start_time,
      end_time: entry.end_time,
      break_minutes: entry.break_minutes || 0,
      entry_type: entry.entry_type || 'work',
      notes: entry.notes || ''
    });
    setShowEntryDialog(true);
  };

  const getSelectedTrainerInfo = () => {
    const trainerData = trainers.find(t => t.trainer.id === selectedTrainer);
    return trainerData?.trainer;
  };

  const getMonthName = (month) => {
    const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 
                    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    return months[month - 1];
  };

  const navigateMonth = (direction) => {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
    loadHolidays(newYear);
  };

  // PDF Download
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/timetracking/reports/monthly/pdf`, {
        headers,
        params: { trainer_id: selectedTrainer, month: selectedMonth, year: selectedYear },
        responseType: 'blob'
      });
      
      // Erstelle Download-Link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const trainer = getSelectedTrainerInfo();
      const trainerName = trainer?.last_name || 'Unbekannt';
      link.setAttribute('download', `Stundenabrechnung_${trainerName}_${getMonthName(selectedMonth)}_${selectedYear}.pdf`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF heruntergeladen');
    } catch (error) {
      console.error('PDF Download Error:', error);
      toast.error('Fehler beim PDF-Download');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // E-Mail senden
  const handleSendEmail = async () => {
    if (!emailForm.recipient_email) {
      toast.error('Bitte E-Mail-Adresse eingeben');
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/timetracking/reports/monthly/send-email`, {
        trainer_id: selectedTrainer,
        year: selectedYear,
        month: selectedMonth,
        recipient_email: emailForm.recipient_email,
        additional_message: emailForm.additional_message || null
      }, { headers });
      
      toast.success(`Bericht an ${emailForm.recipient_email} gesendet`);
      setShowEmailDialog(false);
      setEmailForm({ recipient_email: '', additional_message: '' });
    } catch (error) {
      console.error('Email Error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim E-Mail-Versand');
    } finally {
      setSendingEmail(false);
    }
  };

  // Stunden-Zusammenfassung manuell an die Buchhaltung (ERP) senden.
  // Ohne ausgewählten Trainer werden ALLE Mitarbeiter für den angezeigten Monat gesendet.
  const handleSendToAccounting = async () => {
    const selected = selectedTrainer
      ? trainers.find(({ trainer }) => trainer.id === selectedTrainer)?.trainer
      : null;
    const scope = selected
      ? `für ${`${selected.first_name || ''} ${selected.last_name || ''}`.trim()}`
      : 'für ALLE Mitarbeiter';
    if (!window.confirm(
      `Stunden-Zusammenfassung ${scope} für ${getMonthName(selectedMonth)} ${selectedYear} an die Buchhaltung (ERP) senden?`
    )) return;

    setSendingAccounting(true);
    try {
      const res = await axios.post(
        `${API}/timetracking/worktime/send-to-accounting`,
        {},
        {
          headers,
          params: {
            year: selectedYear,
            month: selectedMonth,
            ...(selectedTrainer ? { trainer_id: selectedTrainer } : {}),
          },
        }
      );
      const d = res.data || {};
      if (d.warning) {
        toast.info(d.warning);
      } else {
        const parts = [`${d.sent || 0} gesendet`];
        if (d.failed) parts.push(`${d.failed} fehlgeschlagen (Retry geplant)`);
        toast.success(`An Buchhaltung übermittelt: ${parts.join(', ')}`);
      }
    } catch (error) {
      console.error('Accounting Sync Error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Senden an die Buchhaltung');
    } finally {
      setSendingAccounting(false);
    }
  };

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'} data-testid="timetracking-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Clock className="text-teal-600" />
            Stundenerfassung
          </h1>
          <p className="text-gray-500">Arbeitszeiten, Ferien und Abwesenheiten verwalten</p>
        </div>
        )}
        {/* Trainer Selector */}
        <div className="flex items-center gap-4">
          <Select value={selectedTrainer || ''} onValueChange={setSelectedTrainer}>
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Trainer auswählen" />
            </SelectTrigger>
            <SelectContent>
              {trainers.map(({ trainer }) => (
                <SelectItem key={trainer.id} value={trainer.id}>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {trainer.first_name} {trainer.last_name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={() => setShowSettingsDialog(true)} data-testid="open-settings-btn">
            <Settings className="w-4 h-4 mr-2" />
            Einstellungen
          </Button>

          {isAdmin && (
            <Button
              variant="outline"
              onClick={handleSendToAccounting}
              disabled={sendingAccounting}
              data-testid="send-to-accounting-btn"
              title={selectedTrainer
                ? 'Stunden des gewählten Mitarbeiters für den angezeigten Monat ans ERP senden'
                : 'Stunden ALLER Mitarbeiter für den angezeigten Monat ans ERP senden'}
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingAccounting ? 'Sende...' : 'An Buchhaltung senden'}
            </Button>
          )}
        </div>
      </div>

      {/* Month Navigation */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigateMonth(-1)}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-xl font-semibold">
              {getMonthName(selectedMonth)} {selectedYear}
            </h2>
            <Button variant="ghost" onClick={() => navigateMonth(1)}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {mode !== 'payroll' && monthlyReport && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Soll-Stunden</p>
                  <p className="text-2xl font-bold text-blue-900">{monthlyReport.adjusted_target_hours}h</p>
                </div>
                <Clock className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Ist-Stunden</p>
                  <p className="text-2xl font-bold text-green-900">{monthlyReport.actual_hours}h</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={`bg-gradient-to-br ${monthlyReport.overtime_hours >= 0 ? 'from-teal-50 to-teal-100 border-teal-200' : 'from-red-50 to-red-100 border-red-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${monthlyReport.overtime_hours >= 0 ? 'text-teal-600' : 'text-red-600'}`}>
                    {monthlyReport.overtime_hours >= 0 ? 'Überstunden' : 'Minusstunden'}
                  </p>
                  <p className={`text-2xl font-bold ${monthlyReport.overtime_hours >= 0 ? 'text-teal-900' : 'text-red-900'}`}>
                    {monthlyReport.overtime_hours >= 0 ? '+' : ''}{monthlyReport.overtime_hours}h
                  </p>
                </div>
                {monthlyReport.overtime_hours >= 0 ? 
                  <TrendingUp className="w-10 h-10 text-teal-400" /> :
                  <TrendingDown className="w-10 h-10 text-red-400" />
                }
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Gesamtsaldo</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {monthlyReport.total_balance >= 0 ? '+' : ''}{monthlyReport.total_balance}h
                  </p>
                </div>
                <FileText className="w-10 h-10 text-purple-400" />
              </div>
              {monthlyReport.carry_over_from_previous !== 0 && (
                <p className="text-xs text-purple-500 mt-1">
                  {monthlyReport.carry_over_is_initial ? 'Anfangssaldo (Migration)' : 'Übertrag'}: {monthlyReport.carry_over_from_previous >= 0 ? '+' : ''}{monthlyReport.carry_over_from_previous}h
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {mode !== 'payroll' && (
        <TabsList className={`grid w-full ${mode === 'all' ? 'grid-cols-6 lg:w-[720px]' : 'grid-cols-5 lg:w-[600px]'}`}>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Calendar size={16} />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="entries" className="flex items-center gap-2">
            <Clock size={16} />
            Zeiten
          </TabsTrigger>
          <TabsTrigger value="absences" className="flex items-center gap-2">
            <Palmtree size={16} />
            Abwesenheiten
          </TabsTrigger>
          {isAdmin && mode === 'all' && (
            <TabsTrigger value="payroll" className="flex items-center gap-2">
              <CreditCard size={16} />
              Lohn
            </TabsTrigger>
          )}
          <TabsTrigger value="year" className="flex items-center gap-2">
            <CalendarCheck size={16} />
            Jahresübersicht
          </TabsTrigger>
          <TabsTrigger value="holidays" className="flex items-center gap-2">
            <FileText size={16} />
            Feiertage
          </TabsTrigger>
        </TabsList>
        )}

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {monthlyReport && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Arbeitstage Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Monatsübersicht</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-600">Arbeitstage im Monat</span>
                    <span className="font-semibold">{monthlyReport.working_days} Tage</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-600">Feiertage im Monat</span>
                    <span className="font-semibold">{monthlyReport.holidays?.length || 0} Tage</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-600">Ferien genommen</span>
                    <span className="font-semibold">{monthlyReport.absences?.vacation_days || 0} Tage</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-600">Krankheitstage</span>
                    <span className="font-semibold">{monthlyReport.absences?.sick_days || 0} Tage</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">Einträge erfasst</span>
                    <span className="font-semibold">{monthlyReport.entries_count} Einträge</span>
                  </div>
                </CardContent>
              </Card>
              
              {/* Progress & Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Fortschritt</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Erfasste Stunden</span>
                      <span>{Math.round((monthlyReport.actual_hours / monthlyReport.adjusted_target_hours) * 100)}%</span>
                    </div>
                    <Progress 
                      value={Math.min((monthlyReport.actual_hours / monthlyReport.adjusted_target_hours) * 100, 100)} 
                      className="h-3"
                    />
                  </div>
                  
                  <div className="pt-4 space-y-2">
                    <Button className="w-full" onClick={() => setShowEntryDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Neue Zeit erfassen
                    </Button>
                    <Button variant="outline" className="w-full" onClick={() => setShowAbsenceDialog(true)}>
                      <Palmtree className="w-4 h-4 mr-2" />
                      Abwesenheit erfassen
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={handleDownloadPdf}
                        disabled={downloadingPdf}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {downloadingPdf ? 'Lädt...' : 'PDF'}
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => setShowEmailDialog(true)}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        E-Mail
                      </Button>
                    </div>
                    <Button 
                      variant="secondary" 
                      className="w-full"
                      onClick={handleCloseMonth}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Monat abschliessen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Entries Tab */}
        <TabsContent value="entries" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Zeiteinträge</h3>
            <Button onClick={() => { resetEntryForm(); setEditingEntry(null); setShowEntryDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Neuer Eintrag
            </Button>
          </div>
          
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Von</TableHead>
                  <TableHead>Bis</TableHead>
                  <TableHead>Pause</TableHead>
                  <TableHead>Stunden</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Notizen</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                      Keine Einträge für diesen Monat
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium">
                        {new Date(entry.date).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                      </TableCell>
                      <TableCell>{entry.start_time}</TableCell>
                      <TableCell>{entry.end_time}</TableCell>
                      <TableCell>{entry.break_minutes} min</TableCell>
                      <TableCell className="font-semibold">{entry.worked_hours}h</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {ENTRY_TYPES.find(t => t.id === entry.entry_type)?.icon} {ENTRY_TYPES.find(t => t.id === entry.entry_type)?.name || entry.entry_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{entry.notes || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditEntry(entry)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteEntry(entry.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Absences Tab */}
        <TabsContent value="absences" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Abwesenheiten {selectedYear}</h3>
            <Button onClick={() => { resetAbsenceForm(); setShowAbsenceDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Neue Abwesenheit
            </Button>
          </div>
          
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Typ</TableHead>
                  <TableHead>Von</TableHead>
                  <TableHead>Bis</TableHead>
                  <TableHead>Tage</TableHead>
                  <TableHead>Notizen</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {absences.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      Keine Abwesenheiten für dieses Jahr
                    </TableCell>
                  </TableRow>
                ) : (
                  absences.map((absence) => {
                    const type = ABSENCE_TYPES.find(t => t.id === absence.absence_type) || ABSENCE_TYPES[4];
                    return (
                      <TableRow key={absence.id}>
                        <TableCell>
                          <Badge className={type.color}>
                            {type.icon} {type.name}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(absence.start_date).toLocaleDateString('de-CH')}</TableCell>
                        <TableCell>{new Date(absence.end_date).toLocaleDateString('de-CH')}</TableCell>
                        <TableCell className="font-semibold">{absence.days_count} Tage</TableCell>
                        <TableCell className="max-w-[200px] truncate">{absence.notes || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAbsence(absence.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>


        {/* Payroll/Lohn Tab */}
        {isAdmin && (mode === 'all' || mode === 'payroll') && (
        <TabsContent value="payroll" className="space-y-4" data-testid="payroll-tab">
          {!selectedTrainer ? (
            <Alert><AlertDescription>Bitte wähle einen Mitarbeiter aus.</AlertDescription></Alert>
          ) : (
            <>
              {/* Actions */}
              <div className="flex flex-wrap gap-2 items-center justify-between">
                <h3 className="text-lg font-semibold">Lohnabrechnung</h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setPayrollForm(payrollSettings || payrollForm); setShowPayrollSettingsDialog(true); }} data-testid="payroll-settings-btn">
                    <Settings size={14} className="mr-1" /> Lohn-Einstellungen
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setBonusForm(bonusConfig || { tiers: [], sick_day_deduction_fraction: 0.25 }); setShowBonusDialog(true); }} data-testid="bonus-settings-btn">
                    <TrendingUp size={14} className="mr-1" /> Bonus-Stufen
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate('/studios/bonus')} data-testid="standort-bonus-link-btn" title="Standort-Bonus (Geschäftsführung): Umsatz-Bonus je Studio inkl. GF-Aufteilung (z.B. 60/40)">
                    <Building2 size={14} className="mr-1" /> Standort-Bonus (GF)
                  </Button>
                  <Button size="sm" disabled={sendingPayrollEmail || !monthlyPayroll?.bruttolohn} onClick={async () => {
                    setSendingPayrollEmail(true);
                    try {
                      const admin = trainers.find(t => t.role === 'admin');
                      await axios.post(`${API}/timetracking/payroll/send-email`, {
                        trainer_id: selectedTrainer, year: selectedYear, month: selectedMonth,
                        recipient_email: settingsForm?.auto_email_recipient || admin?.email || ''
                      }, { headers });
                      toast.success('Lohnabrechnung per E-Mail gesendet');
                    } catch (e) { toast.error('Fehler: ' + (e.response?.data?.detail || e.message)); }
                    finally { setSendingPayrollEmail(false); }
                  }} data-testid="send-payroll-email-btn">
                    <Send size={14} className="mr-1" /> {sendingPayrollEmail ? 'Sende...' : 'E-Mail senden'}
                  </Button>
                  <Button variant="outline" size="sm" disabled={!selectedTrainer} onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/timetracking/payroll/payslip/${selectedTrainer}/${selectedYear}/${selectedMonth}`, { headers, responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                      window.open(url, '_blank');
                      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
                    } catch (e) { toast.error('PDF-Fehler: ' + (e.response?.data?.detail || e.message)); }
                  }} data-testid="payroll-pdf-btn">
                    <FileText size={14} className="mr-1" /> Lohnabrechnung PDF
                  </Button>
                </div>
              </div>

              {/* Lohnlauf-Automatik an/aus */}
              {payrollAutomation && (
                <div className="flex items-start justify-between gap-4 rounded-lg border bg-muted/40 p-4" data-testid="payroll-automation-card">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Settings size={15} className="text-muted-foreground" />
                      <span className="font-semibold">Lohnlauf-Automatik</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${payrollAutomation.enabled ? 'bg-emerald-500/15 text-emerald-600' : 'bg-muted text-muted-foreground'}`} data-testid="payroll-automation-status">
                        {payrollAutomation.enabled ? 'AN' : 'AUS'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                      Automatischer Versand an die Buchhaltung: <strong>Stundenlöhner am 1.</strong> (Vormonat),
                      <strong> Fixlöhner am 25.</strong> (laufender Monat), <strong>GF-Bonus am 1.</strong> (Vormonat, separat).
                      Manueller Versand („An Buchhaltung senden") bleibt immer möglich.
                    </p>
                  </div>
                  <Switch
                    checked={!!payrollAutomation.enabled}
                    disabled={payrollAutomationSaving}
                    data-testid="payroll-automation-switch"
                    onCheckedChange={async (val) => {
                      setPayrollAutomationSaving(true);
                      try {
                        const res = await axios.put(`${API}/settings/payroll-automation`, { enabled: val }, { headers });
                        setPayrollAutomation(res.data);
                        toast.success(val ? 'Lohnlauf-Automatik eingeschaltet' : 'Lohnlauf-Automatik ausgeschaltet');
                      } catch (e) {
                        toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
                      } finally { setPayrollAutomationSaving(false); }
                    }}
                  />
                </div>
              )}

              {monthlyPayroll ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Left: Lohnübersicht */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Lohnübersicht</CardTitle>
                      <CardDescription>{monthlyPayroll.trainer?.first_name} {monthlyPayroll.trainer?.last_name}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {monthlyPayroll.wage_type === 'hourly' ? (
                        <div className="flex justify-between py-1 border-b" data-testid="payroll-hourly-line">
                          <span>Stundenlohn <span className="text-xs text-muted-foreground">({monthlyPayroll.hourly_wage?.toFixed(2)} CHF × {monthlyPayroll.actual_hours?.toFixed(2)} h)</span></span>
                          <span className="font-medium">{monthlyPayroll.grundlohn?.toFixed(2)} CHF</span>
                        </div>
                      ) : (
                        <div className="flex justify-between py-1 border-b"><span>Monatslohn</span><span className="font-medium">{monthlyPayroll.monthly_salary?.toFixed(2)} CHF</span></div>
                      )}
                      {monthlyPayroll.familienzulage > 0 && (
                        <div className="flex justify-between py-1 border-b"><span>Familienzulage</span><span className="text-green-600">+{monthlyPayroll.familienzulage?.toFixed(2)} CHF</span></div>
                      )}
                      {monthlyPayroll.bonus?.final_bonus > 0 && (
                        <div className="flex justify-between py-1 border-b">
                          <span>Bonus <span className="text-xs text-muted-foreground">(Mitarbeiter-Stufen · Umsatz: {monthlyPayroll.bonus.monthly_revenue?.toFixed(0)} CHF)</span></span>
                          <span className="text-green-600">+{monthlyPayroll.bonus.final_bonus?.toFixed(2)} CHF</span>
                        </div>
                      )}
                      {monthlyPayroll.bonus?.sick_deduction_amount > 0 && (
                        <div className="flex justify-between py-1 border-b text-red-500">
                          <span>Krankheitsabzug Bonus ({monthlyPayroll.bonus.sick_days} Tage)</span>
                          <span>-{monthlyPayroll.bonus.sick_deduction_amount?.toFixed(2)} CHF</span>
                        </div>
                      )}
                      {(monthlyPayroll.studio_bonus?.studios || [])
                        .filter((s) => s.personal_bonus_chf > 0)
                        .map((s) => (
                          <div key={s.studio_id} className="flex justify-between py-1 border-b" data-testid={`payroll-studio-bonus-${s.studio_id}`}>
                            <span>Standort-Bonus: {s.studio_name} <span className="text-xs text-muted-foreground">({s.mode === 'fixed' ? 'Fix' : 'Stufe'} {s.percent}%{s.share_percent !== 100 ? `, Anteil ${s.share_percent}%` : ''})</span></span>
                            <span className="text-amber-600">+{s.personal_bonus_chf?.toFixed(2)} CHF</span>
                          </div>
                        ))}
                      <div className="flex justify-between py-2 border-b-2 border-black font-bold text-lg">
                        <span>Bruttolohn</span><span>{monthlyPayroll.bruttolohn?.toFixed(2)} CHF</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Right: Abzüge & Nettolohn — im CRM berechnet (Schalter) ODER ERP-Hinweis */}
                  <Card data-testid="payroll-deductions-note-card">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <CardTitle className="text-base">Abzüge &amp; Nettolohn</CardTitle>
                          <CardDescription>{monthlyPayroll.nettolohn != null ? 'Berechnet im CRM' : 'Berechnung im Lohnsystem'}</CardDescription>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() => navigate('/payroll/deductions')}
                            data-testid="payroll-year-rates-link-btn"
                            title="AHV/ALV-Jahres-Sätze zentral pflegen"
                          >
                            <Percent size={13} className="mr-1" /> Jahres-Sätze pflegen
                          </Button>
                          <span className="text-xs text-muted-foreground">Im CRM berechnen</span>
                          <Switch
                            data-testid="payroll-deductions-toggle"
                            checked={monthlyPayroll.nettolohn != null}
                            onCheckedChange={async (v) => {
                              try {
                                await axios.put(`${API}/timetracking/payroll/deductions-enabled`, { enabled: v }, { headers });
                                toast.success(v ? 'CRM-Abzüge aktiviert' : 'CRM-Abzüge deaktiviert');
                                loadData();
                              } catch (e) { toast.error('Fehler: ' + (e.response?.data?.detail || e.message)); }
                            }}
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {monthlyPayroll.nettolohn != null ? (
                        <div className="space-y-1 text-sm" data-testid="payroll-deductions-breakdown">
                          {[
                            ['ahv_iv_eo', 'AHV/IV/EO', 'ahv_iv_eo_rate'],
                            ['alv', 'ALV', 'alv_rate'],
                            ['nbuv', 'NBUV', 'nbuv_rate'],
                            ['uvgz', 'UVGZ', 'uvgz_rate'],
                            ['ktg', 'KTG', 'ktg_rate'],
                            ['bvg', 'BVG / PK', null],
                            ['quellensteuer', 'Quellensteuer', null],
                          ].filter(([k]) => (monthlyPayroll.deductions?.[k] || 0) > 0).map(([k, label, rateKey]) => (
                            <div key={k} className="flex justify-between py-1 border-b">
                              <span>{label}{rateKey && monthlyPayroll.deduction_rates?.[rateKey] ? <span className="text-xs text-muted-foreground"> ({monthlyPayroll.deduction_rates[rateKey]}%)</span> : null}</span>
                              <span className="text-red-600">-{monthlyPayroll.deductions[k].toFixed(2)} CHF</span>
                            </div>
                          ))}
                          {monthlyPayroll.total_deductions > 0 && (
                            <div className="flex justify-between py-1 font-medium">
                              <span>Total Abzüge</span><span className="text-red-600">-{monthlyPayroll.total_deductions?.toFixed(2)} CHF</span>
                            </div>
                          )}
                          <p className="text-[11px] text-muted-foreground pt-1">Ans ERP geht weiterhin nur der Bruttolohn. Sätze:{' '}
                            <button
                              type="button"
                              onClick={() => navigate('/payroll/deductions')}
                              className="underline underline-offset-2 hover:text-foreground transition-colors"
                              data-testid="payroll-year-rates-link-text"
                            >„Lohn-Einstellungen" (Jahres-Sätze)</button>, BVG/Quellensteuer pro Mitarbeiter.</p>
                        </div>
                      ) : (
                        <div className="rounded-lg bg-muted/60 border p-4 text-sm text-muted-foreground" data-testid="payroll-erp-note">
                          Die Sozialversicherungs-Abzüge (AHV/IV/EO, ALV, NBUV, UVGZ, KTG, BVG)
                          und der <strong>Nettolohn</strong> werden im Lohnsystem (ERP/Buchhaltung)
                          berechnet. Das CRM übermittelt dorthin nur den <strong>Bruttolohn</strong>.
                        </div>
                      )}
                      <div className="flex justify-between py-3 bg-black text-white rounded-lg px-3 text-lg font-bold" data-testid="bruttolohn-display">
                        <span>Bruttolohn</span><span>{monthlyPayroll.bruttolohn?.toFixed(2)} CHF</span>
                      </div>
                      {monthlyPayroll.nettolohn != null && (
                        <div className="flex justify-between py-3 bg-emerald-600 text-white rounded-lg px-3 text-lg font-bold" data-testid="nettolohn-display">
                          <span>Nettolohn</span><span>{monthlyPayroll.nettolohn?.toFixed(2)} CHF</span>
                        </div>
                      )}
                      {monthlyPayroll.bank_iban && (
                        <p className="text-xs text-muted-foreground mt-2">IBAN: {monthlyPayroll.bank_iban} {monthlyPayroll.bank_name ? `(${monthlyPayroll.bank_name})` : ''}</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Bonus Details */}
                  {bonusConfig?.tiers?.length > 0 && (
                    <Card className="lg:col-span-2">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Bonus-Stufen (Mitarbeiter · CHF-Beträge)</CardTitle>
                        <CardDescription>
                          Persönlicher Umsatz-Bonus dieses Mitarbeiters (CHF, gesamter Umsatz). Der
                          umsatzabhängige <strong>Standort-Bonus in %</strong> für Geschäftsführer wird separat
                          unter Einstellungen → „Standort-Bonus (GF)" gepflegt. Aktueller Monatsumsatz: {monthlyPayroll.bonus?.monthly_revenue?.toFixed(0)} CHF
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Von (CHF)</TableHead>
                              <TableHead>Bis (CHF)</TableHead>
                              <TableHead>Bonus (CHF)</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {bonusConfig.tiers.map((tier, i) => {
                              const matched = monthlyPayroll.bonus?.matched_tier &&
                                tier.min_revenue === monthlyPayroll.bonus.matched_tier.min_revenue;
                              return (
                                <TableRow key={i} className={matched ? 'bg-green-50' : ''}>
                                  <TableCell>{tier.min_revenue?.toLocaleString()}</TableCell>
                                  <TableCell>{tier.max_revenue?.toLocaleString()}</TableCell>
                                  <TableCell className="font-medium">{tier.bonus_amount?.toFixed(0)}</TableCell>
                                  <TableCell>{matched ? <Badge className="bg-green-500">Aktiv</Badge> : <Badge variant="outline">-</Badge>}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <Alert><AlertDescription>Keine Lohneinstellungen konfiguriert. Bitte zuerst die Lohn-Einstellungen für diesen Mitarbeiter hinterlegen.</AlertDescription></Alert>
              )}
            </>
          )}
        </TabsContent>
        )}


        {/* Year Summary Tab */}
        <TabsContent value="year" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Jahresübersicht {selectedYear}</h3>
            {yearSummary && !yearSummary.is_closed && (
              <Button 
                onClick={openYearCloseDialog}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Lock className="w-4 h-4 mr-2" />
                Jahr {selectedYear} abschliessen
              </Button>
            )}
          </div>

          {/* Year Summary Cards */}
          {yearSummary && (
            <>
              {yearSummary.is_closed && (
                <Alert className="bg-amber-50 border-amber-200">
                  <Lock className="w-4 h-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    <strong>Jahr {selectedYear} ist abgeschlossen.</strong> Der Übertrag von {yearSummary.closing?.carry_over_hours >= 0 ? '+' : ''}{yearSummary.closing?.carry_over_hours}h wurde ins Jahr {selectedYear + 1} übernommen.
                    {yearSummary.closing?.note && <span className="block mt-1 text-sm">Notiz: {yearSummary.closing.note}</span>}
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Soll-Stunden (Jahr)</p>
                        <p className="text-2xl font-bold text-blue-900">{yearSummary.adjusted_target_hours?.toFixed(1) || 0}h</p>
                      </div>
                      <Clock className="w-10 h-10 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Ist-Stunden (Jahr)</p>
                        <p className="text-2xl font-bold text-green-900">{yearSummary.total_actual_hours?.toFixed(1) || 0}h</p>
                      </div>
                      <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className={`bg-gradient-to-br ${yearSummary.overtime_hours >= 0 ? 'from-teal-50 to-teal-100 border-teal-200' : 'from-red-50 to-red-100 border-red-200'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm font-medium ${yearSummary.overtime_hours >= 0 ? 'text-teal-600' : 'text-red-600'}`}>
                          {yearSummary.overtime_hours >= 0 ? 'Überstunden' : 'Minusstunden'}
                        </p>
                        <p className={`text-2xl font-bold ${yearSummary.overtime_hours >= 0 ? 'text-teal-900' : 'text-red-900'}`}>
                          {yearSummary.overtime_hours >= 0 ? '+' : ''}{yearSummary.overtime_hours?.toFixed(1) || 0}h
                        </p>
                      </div>
                      {yearSummary.overtime_hours >= 0 ?
                        <TrendingUp className="w-10 h-10 text-teal-400" /> :
                        <TrendingDown className="w-10 h-10 text-red-400" />
                      }
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Gesamtsaldo {selectedYear}</p>
                        <p className="text-2xl font-bold text-purple-900">
                          {yearSummary.total_balance >= 0 ? '+' : ''}{yearSummary.total_balance?.toFixed(1) || 0}h
                        </p>
                      </div>
                      <FileText className="w-10 h-10 text-purple-400" />
                    </div>
                    {yearSummary.carry_over_from_previous !== 0 && (
                      <p className="text-xs text-purple-500 mt-1">
                        Übertrag Vorjahr: {yearSummary.carry_over_from_previous >= 0 ? '+' : ''}{yearSummary.carry_over_from_previous}h
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Ferien & Krankheit */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Palmtree className="w-5 h-5 text-blue-600" />
                      Ferien {selectedYear}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-600">Jahresanspruch</span>
                      <span className="font-semibold">{yearSummary.vacation_entitlement_days ?? 25} Tage</span>
                    </div>
                    {(yearSummary.vacation_carryover_in_days || 0) !== 0 && (
                      <div className="flex justify-between items-center py-2 border-b">
                        <span className="text-gray-600">Übertrag Vorjahr</span>
                        <span className="font-semibold text-amber-700">
                          {yearSummary.vacation_carryover_in_days >= 0 ? '+' : ''}{yearSummary.vacation_carryover_in_days} Tage
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-600">Bezogene Ferientage</span>
                      <span className="font-semibold">{yearSummary.total_vacation_days || 0} Tage</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-600">Krankheitstage</span>
                      <span className="font-semibold">{yearSummary.total_sick_days || 0} Tage</span>
                    </div>
                    <div className="flex justify-between items-center py-2 pt-3" data-testid="year-vacation-remaining">
                      <span className="text-gray-800 font-medium">Verbleibende Ferientage</span>
                      <span className={`font-bold text-lg ${(yearSummary.vacation_remaining_days ?? 0) >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                        {yearSummary.vacation_remaining_days ?? 0} Tage
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Jahresabschlüsse */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CalendarCheck className="w-5 h-5 text-amber-600" />
                      Jahresabschlüsse
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {yearClosings.length === 0 ? (
                      <p className="text-gray-500 text-sm">Noch keine Jahresabschlüsse vorhanden.</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Jahr</TableHead>
                            <TableHead>Endsaldo</TableHead>
                            <TableHead>Übertrag</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {yearClosings.map((closing) => (
                            <TableRow key={closing.id}>
                              <TableCell className="font-medium">{closing.year}</TableCell>
                              <TableCell>
                                <span className={closing.final_balance_hours >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {closing.final_balance_hours >= 0 ? '+' : ''}{closing.final_balance_hours}h
                                </span>
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                                  <ArrowRightLeft className="w-3 h-3" />
                                  {closing.carry_over_hours >= 0 ? '+' : ''}{closing.carry_over_hours}h
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDeleteYearClosing(closing.year)}
                                  title="Jahresabschluss löschen"
                                >
                                  <Unlock className="w-4 h-4 text-amber-600" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Holidays Tab */}
        <TabsContent value="holidays" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Gesetzliche Feiertage {selectedYear} (Kanton Zürich)</CardTitle>
              <CardDescription>Diese Tage werden bei der Berechnung der Soll-Stunden automatisch berücksichtigt</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {holidays.map((holiday) => (
                  <div key={holiday.date} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{holiday.name}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(holiday.date).toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Entry Dialog */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Eintrag bearbeiten' : 'Neue Zeit erfassen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Datum</Label>
              <Input 
                type="date" 
                value={entryForm.date}
                onChange={(e) => setEntryForm({...entryForm, date: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Von</Label>
                <Input 
                  type="time" 
                  value={entryForm.start_time}
                  onChange={(e) => setEntryForm({...entryForm, start_time: e.target.value})}
                />
              </div>
              <div>
                <Label>Bis</Label>
                <Input 
                  type="time" 
                  value={entryForm.end_time}
                  onChange={(e) => setEntryForm({...entryForm, end_time: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Pause (Minuten)</Label>
              <Input 
                type="number" 
                value={entryForm.break_minutes}
                onChange={(e) => setEntryForm({...entryForm, break_minutes: parseInt(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>Typ</Label>
              <Select value={entryForm.entry_type} onValueChange={(v) => setEntryForm({...entryForm, entry_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTRY_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.icon} {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notizen</Label>
              <Textarea 
                value={entryForm.notes}
                onChange={(e) => setEntryForm({...entryForm, notes: e.target.value})}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntryDialog(false)}>Abbrechen</Button>
            <Button onClick={editingEntry ? handleUpdateEntry : handleCreateEntry}>
              {editingEntry ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Absence Dialog */}
      <Dialog open={showAbsenceDialog} onOpenChange={setShowAbsenceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Abwesenheit erfassen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Typ</Label>
              <Select value={absenceForm.absence_type} onValueChange={(v) => setAbsenceForm({...absenceForm, absence_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ABSENCE_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.icon} {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Von</Label>
                <Input 
                  type="date" 
                  value={absenceForm.start_date}
                  onChange={(e) => setAbsenceForm({...absenceForm, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Bis</Label>
                <Input 
                  type="date" 
                  value={absenceForm.end_date}
                  onChange={(e) => setAbsenceForm({...absenceForm, end_date: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Notizen</Label>
              <Textarea 
                value={absenceForm.notes}
                onChange={(e) => setAbsenceForm({...absenceForm, notes: e.target.value})}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAbsenceDialog(false)}>Abbrechen</Button>
            <Button onClick={handleCreateAbsence}>Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Trainer-Einstellungen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Wochenstunden (Soll)</Label>
              <Input 
                type="number" 
                step="0.5"
                value={settingsForm.weekly_hours}
                onChange={(e) => setSettingsForm({...settingsForm, weekly_hours: parseFloat(e.target.value) || 42})}
              />
            </div>
            <div>
              <Label>Ferientage pro Jahr</Label>
              <Input 
                type="number" 
                value={settingsForm.vacation_days_per_year}
                onChange={(e) => setSettingsForm({...settingsForm, vacation_days_per_year: parseInt(e.target.value) || 25})}
              />
            </div>
            <div>
              <Label>Arbeitsbeginn</Label>
              <Input 
                type="date" 
                value={settingsForm.start_date || ''}
                onChange={(e) => setSettingsForm({...settingsForm, start_date: e.target.value})}
              />
            </div>
            
            {/* Anfangssaldo für Migration */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4 text-amber-600" />
                Anfangssaldo (Migration)
              </h4>
              <p className="text-xs text-gray-500 mb-3">
                Nutzen Sie diese Felder, um Stundensalden aus einem alten System zu übernehmen.
              </p>
              <div className="space-y-3">
                <div>
                  <Label>Anfangssaldo (Stunden)</Label>
                  <Input 
                    type="number" 
                    step="0.5"
                    placeholder="z.B. -50 oder +30"
                    value={settingsForm.initial_balance_hours || 0}
                    onChange={(e) => setSettingsForm({...settingsForm, initial_balance_hours: parseFloat(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Positiv = Überstunden, Negativ = Minusstunden
                  </p>
                </div>
                <div>
                  <Label>Stichtag für Anfangssaldo</Label>
                  <Input 
                    type="date" 
                    value={settingsForm.initial_balance_date || ''}
                    onChange={(e) => setSettingsForm({...settingsForm, initial_balance_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Bemerkung (optional)</Label>
                  <Input 
                    type="text" 
                    placeholder="z.B. 'Übertrag aus altem System'"
                    value={settingsForm.initial_balance_note || ''}
                    onChange={(e) => setSettingsForm({...settingsForm, initial_balance_note: e.target.value})}
                  />
                </div>
                <div className="pt-3 border-t border-dashed">
                  <Label className="flex items-center gap-2">
                    <Palmtree className="w-4 h-4 text-blue-600" />
                    Ferien-Übertrag aus Vorjahr (Tage)
                  </Label>
                  <Input 
                    type="number" 
                    step="0.5"
                    placeholder="z.B. 5"
                    data-testid="settings-vacation-carryover-input"
                    value={settingsForm.vacation_carryover_days || 0}
                    onChange={(e) => setSettingsForm({...settingsForm, vacation_carryover_days: parseFloat(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Nicht bezogene Ferientage aus dem alten System bzw. Vorjahr. Werden zum
                    Jahresanspruch addiert. Der Jahres-Rest wird automatisch als Übertrag
                    ins nächste Jahr vorgeschlagen.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Auto-E-Mail Einstellungen */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4 text-teal-600" />
                Automatischer Monatsversand
              </h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="auto_email_enabled"
                    checked={settingsForm.auto_email_enabled || false}
                    onChange={(e) => setSettingsForm({...settingsForm, auto_email_enabled: e.target.checked})}
                    className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <Label htmlFor="auto_email_enabled" className="text-sm font-normal cursor-pointer">
                    Monatsbericht automatisch am 1. des Monats senden
                  </Label>
                </div>
                {settingsForm.auto_email_enabled && (
                  <div>
                    <Label>E-Mail-Adresse für automatischen Versand *</Label>
                    <Input 
                      type="email" 
                      placeholder="buchhaltung@firma.ch"
                      value={settingsForm.auto_email_recipient || ''}
                      onChange={(e) => setSettingsForm({...settingsForm, auto_email_recipient: e.target.value})}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Der Bericht für den Vormonat wird jeden 1. um 07:00 Uhr automatisch versendet.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSaveSettings}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-teal-600" />
              Monatsbericht versenden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Trainer:</strong> {getSelectedTrainerInfo()?.first_name} {getSelectedTrainerInfo()?.last_name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Zeitraum:</strong> {getMonthName(selectedMonth)} {selectedYear}
              </p>
              {monthlyReport && (
                <p className="text-sm text-gray-600">
                  <strong>Saldo:</strong> <span className={monthlyReport.total_balance >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {monthlyReport.total_balance >= 0 ? '+' : ''}{monthlyReport.total_balance}h
                  </span>
                </p>
              )}
            </div>
            <div>
              <Label>E-Mail-Adresse des Empfängers *</Label>
              <Input 
                type="email" 
                placeholder="buchhaltung@firma.ch"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({...emailForm, recipient_email: e.target.value})}
              />
            </div>
            <div>
              <Label>Nachricht (optional)</Label>
              <Textarea 
                placeholder="Zusätzliche Informationen für den Buchhalter..."
                value={emailForm.additional_message}
                onChange={(e) => setEmailForm({...emailForm, additional_message: e.target.value})}
                rows={3}
              />
            </div>
            <p className="text-xs text-gray-500">
              Der vollständige Monatsbericht wird als PDF-Anhang versendet.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail}>
              {sendingEmail ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Senden...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Senden
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Year-End Closing Dialog */}
      <Dialog open={showYearCloseDialog} onOpenChange={setShowYearCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              Jahresabschluss {selectedYear}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert className="bg-amber-50 border-amber-200">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                Der Jahresabschluss überträgt den Stundensaldo ins neue Jahr. Bitte prüfen Sie die Werte sorgfältig.
              </AlertDescription>
            </Alert>

            {yearSummary && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Berechneter Endsaldo {selectedYear}:</span>
                  <span className={`font-semibold ${yearSummary.total_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {yearSummary.total_balance >= 0 ? '+' : ''}{yearSummary.total_balance?.toFixed(1)}h
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  Soll: {yearSummary.adjusted_target_hours?.toFixed(1)}h | Ist: {yearSummary.total_actual_hours?.toFixed(1)}h
                </div>
                <div className="flex justify-between pt-2 mt-1 border-t border-gray-200">
                  <span className="text-gray-600">Ferien-Übertrag ins Jahr {selectedYear + 1}:</span>
                  <span className={`font-semibold ${(yearSummary.vacation_remaining_days ?? 0) >= 0 ? 'text-blue-700' : 'text-red-600'}`} data-testid="yearclose-vacation-carryover">
                    {yearSummary.vacation_remaining_days ?? 0} Tage
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  Anspruch {yearSummary.vacation_entitlement_days ?? 25}
                  {(yearSummary.vacation_carryover_in_days || 0) !== 0 ? ` + Übertrag ${yearSummary.vacation_carryover_in_days}` : ''}
                  {' '}- bezogen {yearSummary.total_vacation_days || 0} (wird automatisch übernommen)
                </div>
              </div>
            )}

            <div>
              <Label>Bestätigter Endsaldo (Stunden)</Label>
              <Input 
                type="number" 
                step="0.5"
                value={yearCloseForm.final_balance_hours}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setYearCloseForm({
                    ...yearCloseForm, 
                    final_balance_hours: value,
                    carry_over_hours: value  // Standardmässig gleich
                  });
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                Kann bei Bedarf vom berechneten Wert abweichen.
              </p>
            </div>

            <div>
              <Label>Übertrag ins Jahr {selectedYear + 1} (Stunden)</Label>
              <Input 
                type="number" 
                step="0.5"
                value={yearCloseForm.carry_over_hours}
                onChange={(e) => setYearCloseForm({...yearCloseForm, carry_over_hours: parseFloat(e.target.value) || 0})}
              />
              <p className="text-xs text-gray-500 mt-1">
                Dieser Wert wird als Anfangssaldo für {selectedYear + 1} verwendet.
              </p>
            </div>

            <div>
              <Label>Bemerkung (optional)</Label>
              <Textarea 
                value={yearCloseForm.note}
                onChange={(e) => setYearCloseForm({...yearCloseForm, note: e.target.value})}
                placeholder="z.B. 'Vereinbarte Auszahlung von 20h'"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowYearCloseDialog(false)}>Abbrechen</Button>
            <Button 
              onClick={handleCloseYear} 
              disabled={closingYear}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {closingYear ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Schliesse ab...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Jahr abschliessen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Settings Dialog */}
      <Dialog open={showPayrollSettingsDialog} onOpenChange={setShowPayrollSettingsDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lohn-Einstellungen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Lohnart</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={payrollForm.wage_type || 'fixed'}
                  onChange={e => setPayrollForm({...payrollForm, wage_type: e.target.value})}
                  data-testid="payroll-wage-type-select"
                >
                  <option value="fixed">Fixlohn (Monatslohn)</option>
                  <option value="hourly">Stundenlohn</option>
                </select>
              </div>
              {payrollForm.wage_type === 'hourly' ? (
                <div>
                  <Label>Stundenlohn (CHF/h)</Label>
                  <Input type="number" step="0.01" value={payrollForm.hourly_wage || ''} onChange={e => setPayrollForm({...payrollForm, hourly_wage: parseFloat(e.target.value) || 0})} data-testid="payroll-hourly-wage-input" />
                </div>
              ) : (
                <div>
                  <Label>Monatslohn (CHF)</Label>
                  <Input type="number" step="0.01" value={payrollForm.monthly_salary || ''} onChange={e => setPayrollForm({...payrollForm, monthly_salary: parseFloat(e.target.value) || 0})} data-testid="payroll-salary-input" />
                </div>
              )}
              <div>
                <Label>Familienzulage (CHF)</Label>
                <Input type="number" step="0.01" value={payrollForm.familienzulage || ''} onChange={e => setPayrollForm({...payrollForm, familienzulage: parseFloat(e.target.value) || 0})} data-testid="payroll-familienzulage-input" />
              </div>
            </div>
            <div>
              <Label>PK / BVG Arbeitgeberanteil (CHF / Monat)</Label>
              <Input type="number" step="0.01" value={payrollForm.pk_employer_amount || ''} onChange={e => setPayrollForm({...payrollForm, pk_employer_amount: parseFloat(e.target.value) || 0})} data-testid="payroll-pk-employer-input" />
              <p className="text-[11px] text-gray-400 mt-1">Fliesst als Arbeitgeberkosten in die Trainer-Rentabilität (Vollkosten) ein.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 border-t pt-3">
              <div>
                <Label>BVG / PK Abzug Arbeitnehmer (CHF / Monat)</Label>
                <Input type="number" step="0.01" value={payrollForm.bvg_amount || ''} onChange={e => setPayrollForm({...payrollForm, bvg_amount: parseFloat(e.target.value) || 0})} data-testid="payroll-bvg-amount-input" />
              </div>
              <div>
                <Label>Quellensteuer (%)</Label>
                <Input type="number" step="0.01" value={payrollForm.quellensteuer_rate || ''} onChange={e => { const r = parseFloat(e.target.value) || 0; setPayrollForm({...payrollForm, quellensteuer_rate: r, quellensteuer_enabled: r > 0}); }} data-testid="payroll-quellensteuer-input" />
              </div>
            </div>
            <p className="text-[11px] text-gray-400">Diese Abzüge werden nur angewendet, wenn der Schalter „Im CRM berechnen" (Lohn-Tab) aktiv ist. Leer/0 = kein Abzug.</p>
            <h4 className="font-semibold text-sm mt-4 border-t pt-3">Bankdaten</h4>
            <div><Label>AHV-Nr. (für Buchhaltung)</Label><Input placeholder="756.XXXX.XXXX.XX" value={payrollForm.ahv_number || ''} onChange={e => setPayrollForm({...payrollForm, ahv_number: e.target.value})} data-testid="payroll-ahv-number-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Bank</Label><Input value={payrollForm.bank_name || ''} onChange={e => setPayrollForm({...payrollForm, bank_name: e.target.value})} /></div>
              <div><Label>IBAN</Label><Input value={payrollForm.bank_iban || ''} onChange={e => setPayrollForm({...payrollForm, bank_iban: e.target.value})} /></div>
            </div>
            <div><Label>Notizen</Label><Textarea value={payrollForm.notes || ''} onChange={e => setPayrollForm({...payrollForm, notes: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayrollSettingsDialog(false)}>Abbrechen</Button>
            <Button onClick={async () => {
              try {
                await axios.put(`${API}/timetracking/payroll/settings/${selectedTrainer}`, payrollForm, { headers });
                toast.success('Lohn-Einstellungen gespeichert');
                setShowPayrollSettingsDialog(false);
                loadData();
              } catch (e) { toast.error('Fehler: ' + (e.response?.data?.detail || e.message)); }
            }} data-testid="save-payroll-settings-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bonus Config Dialog */}
      <Dialog open={showBonusDialog} onOpenChange={setShowBonusDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bonus-Stufen konfigurieren</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Definiere Umsatz-Stufen und den zugehörigen Bonus. Pro Krankheitstag wird ein Bruchteil des Bonus abgezogen.</p>
            <div>
              <Label>Krankheitsabzug pro Tag (Anteil vom Bonus)</Label>
              <Select value={String(bonusForm.sick_day_deduction_fraction)} onValueChange={v => setBonusForm({...bonusForm, sick_day_deduction_fraction: parseFloat(v)})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.25">1/4 (25%)</SelectItem>
                  <SelectItem value="0.2">1/5 (20%)</SelectItem>
                  <SelectItem value="0.1">1/10 (10%)</SelectItem>
                  <SelectItem value="0.5">1/2 (50%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <h4 className="font-semibold text-sm border-t pt-3">Umsatz-Stufen</h4>
            {(bonusForm.tiers || []).map((tier, i) => (
              <div key={i} className="grid grid-cols-4 gap-2 items-end">
                <div><Label className="text-xs">Von (CHF)</Label><Input type="number" value={tier.min_revenue || ''} onChange={e => { const t = [...bonusForm.tiers]; t[i] = {...t[i], min_revenue: parseFloat(e.target.value) || 0}; setBonusForm({...bonusForm, tiers: t}); }} /></div>
                <div><Label className="text-xs">Bis (CHF)</Label><Input type="number" value={tier.max_revenue || ''} onChange={e => { const t = [...bonusForm.tiers]; t[i] = {...t[i], max_revenue: parseFloat(e.target.value) || 0}; setBonusForm({...bonusForm, tiers: t}); }} /></div>
                <div><Label className="text-xs">Bonus (CHF)</Label><Input type="number" value={tier.bonus_amount || ''} onChange={e => { const t = [...bonusForm.tiers]; t[i] = {...t[i], bonus_amount: parseFloat(e.target.value) || 0}; setBonusForm({...bonusForm, tiers: t}); }} /></div>
                <Button variant="ghost" size="sm" onClick={() => { const t = bonusForm.tiers.filter((_, j) => j !== i); setBonusForm({...bonusForm, tiers: t}); }}><Trash2 size={14} /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setBonusForm({...bonusForm, tiers: [...(bonusForm.tiers || []), {min_revenue: 0, max_revenue: 0, bonus_amount: 0}]})} data-testid="add-bonus-tier-btn">
              <Plus size={14} className="mr-1" /> Stufe hinzufügen
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBonusDialog(false)}>Abbrechen</Button>
            <Button onClick={async () => {
              try {
                await axios.put(`${API}/timetracking/payroll/bonus/${selectedTrainer}`, { trainer_id: selectedTrainer, ...bonusForm }, { headers });
                toast.success('Bonus-Konfiguration gespeichert');
                setShowBonusDialog(false);
                loadData();
              } catch (e) { toast.error('Fehler: ' + (e.response?.data?.detail || e.message)); }
            }} data-testid="save-bonus-config-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default TimeTrackingPage;
